/**
 * OTA (over-the-air) updates — fully automatic.
 *
 *   1. Boot → notifyAppReady() ASAP (tells the plugin this bundle launched OK,
 *      so it doesn't roll back).
 *   2. After splash, check the server ONCE per session for a newer bundle.
 *   3. New bundle → download (progress toast) → set() applies it and RELOADS
 *      the WebView immediately (auto-restart — no "reopen later" step).
 *   4. After the reload, boot reads the `applying` marker: if it matches the
 *      now-current version, the update succeeded → "App atualizado · vX" popup.
 *
 * Loop-proof:
 *   - currentVersion = ONLY /version.json from the live bundle (never
 *     plugin.current(), whose internal state lags after a swap).
 *   - Per-version attempt cap: a bundle that never becomes current after
 *     MAX_ATTEMPTS is abandoned (can't loop forever).
 *   - One check per session (inFlight + checkedThisSession).
 */

import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNative, getApiBase } from './api-base';
import { getBundleVersion } from './version';

export interface OtaProgress {
  percent: number;
  state: 'idle' | 'checking' | 'downloading' | 'ready' | 'applied' | 'error';
  pendingVersion?: string;
  notes?: string;
}

type Listener = (p: OtaProgress) => void;
const listeners = new Set<Listener>();
let currentState: OtaProgress = { state: 'idle', percent: 0 };

function emit(next: Partial<OtaProgress>) {
  currentState = { ...currentState, ...next };
  listeners.forEach((fn) => {
    try { fn(currentState); } catch { /* noop */ }
  });
}

export function subscribeOta(fn: Listener): () => void {
  listeners.add(fn);
  fn(currentState);
  return () => { listeners.delete(fn); };
}

export function getOtaState(): OtaProgress {
  return currentState;
}

interface ManifestResp {
  success: boolean;
  data?:
    | { upToDate: true; version: string }
    | {
        upToDate: false;
        version: string; // semver "0.0.N" — capgo download() requires valid semver
        sha?: string; // git short sha (traceability only)
        bundleUrl: string;
        checksum?: string; // sha256 hex of the zip (verified natively by the plugin)
        releasedAt: string;
        mandatory: boolean;
        notes?: string;
      };
}

// Marker for the version we kicked off applying — set RIGHT BEFORE set()
// reloads the WebView. After the reload, boot reads it: if it equals the
// now-current bundle version, the update applied successfully → we show the
// "updated" popup. Survives the reload via localStorage.
const APPLYING_KEY = 'my360.ota.applyingVersion';

function getApplying(): string | null {
  try { return localStorage.getItem(APPLYING_KEY); } catch { return null; }
}
function setApplying(version: string | null) {
  try {
    if (version) localStorage.setItem(APPLYING_KEY, version);
    else localStorage.removeItem(APPLYING_KEY);
  } catch { /* noop */ }
}

// Hard safety net against ANY future update loop: cap download attempts per
// target version. If a bundle is offered but never actually becomes current
// after a couple of tries (e.g. it won't activate on this device), we stop
// trying it instead of re-downloading forever.
const ATTEMPTS_KEY = 'my360.ota.attempts';
const MAX_ATTEMPTS = 2;

function attemptCount(version: string): number {
  try {
    return (JSON.parse(localStorage.getItem(ATTEMPTS_KEY) || '{}')[version] as number) || 0;
  } catch {
    return 0;
  }
}
function bumpAttempt(version: string) {
  try {
    const map = JSON.parse(localStorage.getItem(ATTEMPTS_KEY) || '{}');
    map[version] = (map[version] || 0) + 1;
    localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(map));
  } catch { /* noop */ }
}
function clearAttempts(version: string) {
  try {
    const map = JSON.parse(localStorage.getItem(ATTEMPTS_KEY) || '{}');
    delete map[version];
    localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(map));
  } catch { /* noop */ }
}

/**
 * Atualização por OTA — DESLIGADA.
 *
 * O app passa a se atualizar só pelos canais normais: baixando o APK do site
 * ou pela loja. Trocar o bundle web por fora disso significa que a versão em
 * execução não é a que foi instalada nem a que foi revisada, o que atrapalha
 * tanto o suporte ("qual versão você está usando?" deixa de ter resposta
 * simples) quanto a submissão à loja, onde entrega dinâmica de código é
 * assunto delicado.
 *
 * O código fica no lugar, inerte, porque desligar é uma decisão de produto
 * que pode ser revista — e apagar tudo obrigaria a reescrever um fluxo que
 * custou caro para ficar à prova de laço de reinício.
 *
 * notifyAppReady continua sendo chamado: sem ele o plugin considera o boot
 * fracassado e reverte sozinho no lançamento seguinte.
 */
const OTA_ENABLED = false;

/**
 * Tell the plugin "this bundle launched OK, don't roll back". The
 * @capgo plugin reverts to the previous bundle on next launch UNLESS
 * notifyAppReady() is called within ~10s of boot (we configured 30s
 * via capacitor.config plugin opts). Call exactly once per boot,
 * as early as possible. No-op outside Capacitor.
 *
 * NOTE: deliberately does NOT call CapacitorUpdater.current() or
 * .reset() — both proved to be sources of restart loops. notifyAppReady
 * is enough; the plugin's bookkeeping is its own problem.
 */
export async function markBundleReady(): Promise<void> {
  if (!isNative()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (err) {
    console.warn('[OTA] notifyAppReady failed:', err);
  }
}

/**
 * Check the server for a newer bundle. ONE check per session via
 * inFlight + checkedThisSession. If a bundle is found, download in
 * background and stage for next launch. NEVER reloads the WebView.
 */
let inFlight = false;
let checkedThisSession = false;

export async function checkForOtaUpdate(): Promise<void> {
  if (!OTA_ENABLED) return;
  if (!isNative()) return;
  if (inFlight || checkedThisSession) return;
  inFlight = true;
  try {
    // Source of truth: /version.json baked into the bundle the WebView is
    // actually serving NOW (cache-busted in version.ts). Never plugin.current().
    const currentVersion = (await getBundleVersion()).version;

    // Did we just apply an update? set() reloaded us — if the version we were
    // applying is now the current one, it worked → celebrate with a popup.
    const applying = getApplying();
    if (applying) {
      if (applying === currentVersion) {
        setApplying(null);
        clearAttempts(applying);
        emit({ state: 'applied', percent: 100, pendingVersion: currentVersion });
        checkedThisSession = true;
        return; // fresh on the new bundle — don't immediately re-check/download
      }
      // Marker set but the bundle didn't become current (didn't activate).
      // Fall through and re-evaluate; the per-version attempt cap bounds retries.
    }

    emit({ state: 'checking', percent: 0 });

    const url = `${getApiBase()}/app/ota/manifest?currentVersion=${encodeURIComponent(currentVersion)}`;
    const response = await fetch(url, { method: 'GET', cache: 'no-store' });
    const json = (await response.json()) as ManifestResp;

    if (!json.success || !json.data || json.data.upToDate) {
      setApplying(null);
      emit({ state: 'idle', percent: 0 });
      checkedThisSession = true;
      return;
    }

    const { version, bundleUrl, checksum, notes } = json.data;

    // Safety net: if we've already tried this version MAX_ATTEMPTS times and it
    // still isn't current, give up (prevents any download/restart loop).
    if (attemptCount(version) >= MAX_ATTEMPTS) {
      console.warn(`[OTA] giving up on ${version} after ${MAX_ATTEMPTS} attempts`);
      setApplying(null);
      emit({ state: 'idle', percent: 0 });
      checkedThisSession = true;
      return;
    }
    bumpAttempt(version);

    // Download in the background (small progress toast, never blocks).
    emit({ state: 'downloading', percent: 0, notes, pendingVersion: version });

    const sub = await CapacitorUpdater.addListener('download', (info) => {
      const pct = (info as { percent?: number })?.percent;
      if (typeof pct === 'number') {
        emit({ percent: Math.max(0, Math.min(100, pct)) });
      }
    });

    // Download with the manifest's sha256 checksum when present — the plugin
    // verifies it natively (SHA-256 hex) before installing. Fallback: if the
    // checksum'd download fails (e.g. algorithm/format divergence between
    // server and plugin versions), retry WITHOUT checksum so a hash quirk
    // can never brick OTA delivery (same pattern proven in reformu.lat).
    let newBundle;
    try {
      newBundle = checksum
        ? await CapacitorUpdater.download({ url: bundleUrl, version, checksum })
        : await CapacitorUpdater.download({ url: bundleUrl, version });
    } catch (err) {
      if (!checksum) throw err;
      console.warn('[OTA] download with checksum failed, retrying without:', err);
      newBundle = await CapacitorUpdater.download({ url: bundleUrl, version });
    }
    sub.remove();

    // Mark WHAT we're applying (read after the reload to show the "updated"
    // popup), then APPLY — set() switches the bundle and reloads the WebView
    // immediately. No "reopen later" step: it auto-restarts now.
    setApplying(version);
    emit({ state: 'ready', percent: 100, pendingVersion: version, notes });
    await new Promise<void>((r) => setTimeout(r, 900));
    await CapacitorUpdater.set({ id: newBundle.id });

    checkedThisSession = true; // usually unreachable — set() reloaded us
  } catch (err) {
    console.warn('[OTA] check/download failed:', err);
    emit({ state: 'error', percent: 0 });
    setTimeout(() => emit({ state: 'idle', percent: 0 }), 4000);
  } finally {
    inFlight = false;
  }
}
